/**
 * The default landing screen: `/o/:org/skills`.
 *
 * This is a table, not a dashboard. V1 section 9 asks for search, owner and
 * status filters, a table or list, and a New skill action, and it asks for the
 * default to be usable published skills with "My drafts" explicit. It does not
 * ask for a metrics row, and section 9 closes by forbidding speculative metric
 * cards, so there are none.
 *
 * The count line under the toolbar is not decoration: it says whether the result
 * set was truncated by the row limit, because a table that quietly stops at a
 * page boundary is indistinguishable from a complete answer.
 */

import type { AppShell, ScreenState } from '../app.js';
import * as api from '../api/queries.js';
import { type Result, ok } from '../api/client.js';
import { type RawHtml, html } from '../lib/html.js';
import {
  DRAFT_STATE_LABEL,
  LIFECYCLE_LABEL,
  type PeopleDirectory,
  formatDate,
  personLabel,
} from '../lib/format.js';
import { type SkillsQuery, hasActiveFilters, parseSkillsQuery } from '../lib/router.js';
import { button, docHead, emptyState, failurePanel, skeletonRows, tag } from '../ui/components.js';

const ROW_LIMIT = 50;

const LIBRARY_COLUMNS = ['Name and when to use', 'Owner', 'Rev', 'Status', 'Updated'];

interface DraftView {
  readonly draft: api.DraftRow;
  readonly skill: api.SkillRow | null;
}

export interface LibraryData {
  readonly skills: readonly api.LibrarySkill[];
  readonly drafts: readonly DraftView[];
  readonly truncated: boolean;
}

export async function loadLibrary(app: AppShell, orgId: string): Promise<Result<LibraryData>> {
  const query = parseSkillsQuery(app.state.search);

  if (query.scope === 'drafts') {
    const drafts = await api.listOwnDrafts(app.client, orgId, app.state.session?.userId ?? '');
    if (!drafts.ok) return drafts;
    const skills = await api.loadSkills(app.client, drafts.value.map((row) => row.skill_id));
    if (!skills.ok) return skills;
    const byId = new Map(skills.value.map((skill) => [skill.id, skill]));
    return ok({
      skills: [],
      drafts: drafts.value.map((draft) => ({ draft, skill: byId.get(draft.skill_id) ?? null })),
      truncated: false,
    });
  }

  const skills = await api.listSkills(app.client, orgId, {
    q: query.q,
    owner: query.owner,
    status: query.status,
    limit: ROW_LIMIT,
  });
  if (!skills.ok) return skills;
  return ok({ skills: skills.value.rows, drafts: [], truncated: skills.value.truncated });
}

export function renderLibrary(app: AppShell, org: string, screen: ScreenState): RawHtml {
  if (screen.kind === 'loading' || screen.kind === 'idle') {
    return html`
      ${header(org, 'Loading')}
      <div class="panel">${skeletonRows(LIBRARY_COLUMNS, 6)}</div>
    `;
  }
  if (screen.kind === 'failed') {
    return html`
      ${header(org, 'Unavailable')}
      ${failurePanel(screen.failure, { retry: 'library:reload' })}
    `;
  }

  const data = screen.data as LibraryData;
  const query = parseSkillsQuery(app.state.search);
  const filtered = hasActiveFilters(query);

  return html`
    ${header(org, query.scope === 'drafts' ? 'My drafts' : 'Library')}
    ${toolbar(app.state.directory, query, data)}
    <div class="btn-row">
      ${scopeButton('Published skills', 'published', query.scope)}
      ${scopeButton('My drafts', 'drafts', query.scope)}
      <span class="topbar-spacer"></span>
      ${filtered ? button('Clear filters', 'library:clear-filters', 'quiet') : ''}
    </div>
    ${countLine(query.scope, data, filtered)}
    ${query.scope === 'drafts' ? draftsTable(app, data) : skillsTable(app, data, filtered)}
  `;
}

function header(org: string, section: string): RawHtml {
  return docHead(
    [
      { label: 'Section', value: section },
      { label: 'Organization', value: org },
    ],
    section === 'My drafts' ? 'My drafts' : 'Skills library',
    section === 'My drafts'
      ? 'Drafts you have written. A draft belongs to its author until it is submitted for review.'
      : 'Published skills you are permitted to use. A skill with no current release is not usable and is not listed here.',
  );
}

function toolbar(directory: PeopleDirectory, query: SkillsQuery, data: LibraryData): RawHtml {
  const owners = [...new Set(data.skills.map((row) => row.skill.owner_id))].sort();
  return html`
    <div class="toolbar">
      <div class="toolbar-grow">
        <label class="field-label" for="library-q">Search</label>
        <input
          class="field"
          id="library-q"
          type="search"
          autocomplete="off"
          data-field="library.q"
          value="${query.q}"
          placeholder="Search skill titles"
        />
      </div>
      <label class="field-fieldset">
        <span class="field-label">Owner</span>
        <select class="field" id="library-owner" data-field="library.owner">
          ${option('all', 'Anyone', query.owner)}
          ${owners.map((id) => option(id, personLabel(id, directory), query.owner))}
        </select>
      </label>
      <label class="field-fieldset">
        <span class="field-label">Status</span>
        <select class="field" id="library-status" data-field="library.status">
          ${option('all', 'Any status', query.status)}
          ${option('active', LIFECYCLE_LABEL.active ?? 'Active', query.status)}
          ${option('deprecated', LIFECYCLE_LABEL.deprecated ?? 'Deprecated', query.status)}
          ${option('retired', LIFECYCLE_LABEL.retired ?? 'Retired', query.status)}
        </select>
      </label>
      ${button('New skill', 'library:new-skill', 'primary', {
        // The one modal returns focus to the control that opened it, and the
        // return address is this id — see `noteDialogReturnFocus`. Without it
        // the handler records `null` and Escape leaves focus on `document.body`
        // (measured 2026-10-01, real Chromium).
        id: 'new-skill',
      })}
    </div>
  `;
}

function option(value: string, label: string, selected: string): RawHtml {
  return html`<option value="${value}"${value === selected ? ' selected' : ''}>${label}</option>`;
}

function scopeButton(
  label: string,
  scope: 'published' | 'drafts',
  current: 'published' | 'drafts',
): RawHtml {
  if (scope === current) {
    return html`<span class="tag tag-accent"><span class="glyph" aria-hidden="true">#</span>${label}</span>`;
  }
  return button(label, `library:scope:${scope}`, 'quiet');
}

function countLine(scope: 'published' | 'drafts', data: LibraryData, filtered: boolean): RawHtml {
  if (scope === 'drafts') {
    const open = data.drafts.filter((view) => view.draft.state === 'draft').length;
    const reviewing = data.drafts.filter((view) => view.draft.state === 'in_review').length;
    return html`<p class="filter-summary">
      ${data.drafts.length} of your drafts · ${open} open · ${reviewing} awaiting review
    </p>`;
  }
  const total = data.skills.length;
  return html`<p class="filter-summary">
    ${filtered ? 'Filtered' : 'All'} · ${total} ${total === 1 ? 'skill' : 'skills'}${data.truncated
      ? ` · more exist than the ${ROW_LIMIT} this page loads; narrow the search`
      : ''}
  </p>`;
}

function skillsTable(app: AppShell, data: LibraryData, filtered: boolean): RawHtml {
  if (data.skills.length === 0) {
    return html`
      <div class="panel">
        ${filtered
          ? emptyState({
              title: 'No skills match these filters',
              body: 'Nothing here matches the search, owner and status you have set. Clearing the filters shows every published skill you can use.',
              primary: { label: 'Clear filters', action: 'library:clear-filters' },
              secondary: [],
            })
          : emptyState({
              title: 'No published skills you can use yet',
              body: 'A new skill starts private to the person who made it, so nothing appears here until an administrator grants it to the organization or to you. What you have written yourself is under My drafts.',
              primary: { label: 'New skill', action: 'library:new-skill', id: 'new-skill-empty' },
              secondary: [{ label: 'Administration', action: 'library:goto-admin' }],
            })}
      </div>
    `;
  }
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Published skills</h2>
        <span class="cell-meta">${app.orgSlug}</span>
      </div>
      <div class="table-scroll" data-scroll="library">
        <table class="data">
          <caption>Published skills, most recently updated first</caption>
          <thead>
            <tr>
              <th class="col-name" scope="col">Name and when to use</th>
              <th class="col-owner" scope="col">Owner</th>
              <th class="col-rev" scope="col">Rev</th>
              <th class="col-status" scope="col">Status</th>
              <th class="col-when" scope="col">Updated</th>
            </tr>
          </thead>
          <tbody>
            ${data.skills.map((row) => libraryRow(app, row))}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

function libraryRow(app: AppShell, row: api.LibrarySkill): RawHtml {
  const skill = row.skill;
  const deprecated = skill.lifecycle === 'deprecated';
  return html`
    <tr>
      <td>
        <a class="cell-name" href="/o/${app.orgSlug}/skills/${skill.id}">${skill.title}</a>
        <span class="cell-note">${skill.description ?? 'No description recorded.'}</span>
      </td>
      <td class="cell-meta">${personLabel(skill.owner_id, app.state.directory)}</td>
      <td class="cell-meta">${row.release?.revision ?? 'none'}</td>
      <td>${deprecated ? tag('Deprecated', 'D', 'attention') : tag('Published', 'P', 'accent')}</td>
      <td class="cell-meta">${formatDate(skill.updated_at)}</td>
    </tr>
  `;
}

function draftsTable(app: AppShell, data: LibraryData): RawHtml {
  if (data.drafts.length === 0) {
    return html`
      <div class="panel">
        ${emptyState({
          title: 'You have no drafts',
          body: 'A draft is the revision workspace for a skill. Creating a skill creates an unpublished one, and that is where it starts.',
          primary: { label: 'New skill', action: 'library:new-skill', id: 'new-draft-empty' },
          secondary: [],
        })}
      </div>
    `;
  }
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Your drafts</h2>
        <span class="cell-meta">${data.drafts.length}</span>
      </div>
      <div class="table-scroll" data-scroll="drafts">
        <table class="data">
          <caption>Drafts you have written</caption>
          <thead>
            <tr>
              <th class="col-name" scope="col">Skill and change reason</th>
              <th class="col-owner" scope="col">State</th>
              <th class="col-rev" scope="col">Version</th>
              <th class="col-status" scope="col">Action</th>
              <th class="col-when" scope="col">Updated</th>
            </tr>
          </thead>
          <tbody>
            ${data.drafts.map((view) => draftRow(app, view))}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

/**
 * One draft, one row.
 *
 * WHERE A FAILED PUBLICATION SENDS YOU, and why it is not the editor. V1 §2
 * PUB-04 requires a failed publication to be visible AND retryable, and the
 * retry control lives in exactly one place — the review screen, which is where
 * `publishAvailability` decides whether Publish may be pressed at all. A second
 * copy of that control on the editor would be a second availability rule that
 * could disagree with the first, which is the defect this row used to have in
 * the other direction: `publish_failed` looked terminal, the link went to the
 * editor, and the editor has no way to act on it.
 *
 * So the badge stays a badge and the row grows a link to the review. One
 * control, one availability rule, one place the worker's `last_error` is read
 * from.
 */
function draftRow(app: AppShell, view: DraftView): RawHtml {
  const { draft, skill } = view;
  const reviewHref = `/o/${app.orgSlug}/reviews/${draft.id}`;
  const editHref = skill === null ? `/o/${app.orgSlug}/skills` : `/o/${app.orgSlug}/skills/${skill.id}/edit`;
  const label = DRAFT_STATE_LABEL[draft.state] ?? draft.state;
  // Publication states a person can act on, or is waiting on, all belong on
  // the review screen: the panel is mounted nowhere else.
  const onReviewScreen =
    draft.state === 'in_review' ||
    draft.state === 'approved' ||
    draft.state === 'publishing' ||
    draft.state === 'publish_failed' ||
    draft.state === 'published';
  const href = onReviewScreen ? reviewHref : editHref;
  const action = onReviewScreen
    ? draft.state === 'in_review' || draft.state === 'approved'
      ? 'Open review'
      : 'Open publication'
    : 'Edit';
  return html`
    <tr>
      <td>
        <a class="cell-name" href="${href}">${skill?.title ?? 'A skill you can no longer see'}</a>
        <span class="cell-note">${draft.change_reason ?? 'No change reason recorded.'}</span>
      </td>
      <td>${tag(label, 'D', draft.state === 'publish_failed' ? 'danger' : 'plain')}</td>
      <td class="cell-meta">${draft.version}</td>
      <td>
        <a href="${href}">${action}</a>
        ${draft.state === 'publish_failed'
          ? html`<span class="cell-note">
              Publication failed. The reason and the retry are on the review screen.
            </span>`
          : ''}
      </td>
      <td class="cell-meta">${formatDate(draft.updated_at)}</td>
    </tr>
  `;
}